import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Container,
  Toolbar,
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import BoletiaIcon from '@/components/BoletiaIcon';
import { HEADER } from '@/config';
import cssStyles from '@/utils/cssStyles';
import useOffSetTop from '@/hooks/useOffSetTop';
import useEventContext from '@/contexts/event/useEventContext';
import { formatLocalDate } from '@/locales/dates';
import LanguageSelector from './LanguageSelector';

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  height: HEADER.MOBILE_HEIGHT,
  transition: theme.transitions.create(['height', 'background-color'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('md')]: {
    height: HEADER.MAIN_DESKTOP_HEIGHT,
  },
}));

// ----------------------------------------------------------------------

export default function HeaderDesktop({ showEventInfo = false }) {
  const isOffset = useOffSetTop(1);
  const [eventDate, setEventDate] = useState(null);

  const theme = useTheme();

  const {
    event: {
      event: { name, start_date },
    },
  } = useEventContext();

  useEffect(() => {
    setEventDate(formatLocalDate(start_date));
  }, []);

  return (
    <>
      <AppBar
        sx={{
          background: (theme) => theme.palette.background.paper,
        }}
      >
        <ToolbarStyle
          disableGutters
          sx={{
            ...(isOffset && {
              ...cssStyles(theme).bgBlur(),
              height: { md: HEADER.MAIN_DESKTOP_HEIGHT - 16 },
            }),
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
            }}
          >
            <Container
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '100%',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <BoletiaIcon
                  sx={{
                    width: '180px',
                  }}
                  onClick={() => {
                    window.open(`https://boletia.com`, '_self');
                  }}
                  fill={
                    theme.palette.mode === 'light'
                      ? theme.palette.orange[0]
                      : theme.palette.common.white
                  }
                />
              </Box>
            </Container>
          </Box>
          {showEventInfo && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
              }}
            >
              <Container
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  direction: 'column',
                  height: '100%',
                  width: '100%',
                }}
              >
                <Stack
                  direction="column"
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    component="div"
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: (theme) => theme.palette.grey[800],
                    }}
                  >
                    {name}
                  </Typography>
                  <Typography
                    variant="caption2"
                    component="p"
                    sx={{
                      '&::first-letter': {
                        textTransform: 'capitalize',
                      },
                      'color': (theme) => theme.palette.grey[500],
                    }}
                  >
                    {eventDate}
                  </Typography>
                </Stack>
              </Container>
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
            }}
          >
            <Container
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'right',
                direction: 'column',
                height: '100%',
                width: '100%',
              }}
            >
              <LanguageSelector />
            </Container>
          </Box>
        </ToolbarStyle>
      </AppBar>
    </>
  );
}
